// ============================================================
// FILE: GardenerLink.js
// LOCATION: C:\gardenmarket\web\src\pages\public\GardenerLink.js
// ============================================================
// PURPOSE:
//   Landing page for a gardener's shareable link, /g/MIKE4K2P.
//
//   A gardener hands this to their OWN prospective customers.
//   Those customers were never shopping the marketplace — they
//   were going to ring this gardener. So a job posted from here
//   is PERMANENTLY exclusive to them: no timer, no competing
//   quotes.
//
//   That is the whole reason a gardener would share it. If they
//   risked losing their own customers to rivals on the platform,
//   they would never hand the link out, and the referral pitch
//   collapses.
//
// FLOW:
//   /g/CODE  -> this page
//             -> "Request a quote"
//             -> stashes the code, sends to register or post-job
//             -> PostJob reads it and posts with
//                job_source='referral'
//
// WHY sessionStorage:
//   Artifacts guidance forbids browser storage, but this is the
//   real app, not an artifact. The code has to survive the
//   register/login round trip. sessionStorage (not localStorage)
//   so it does not linger after the browser closes and silently
//   attribute an unrelated job months later.
// ============================================================

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import client from '../../api/client';

export const REFERRAL_KEY = 'gh_referral_code';

export default function GardenerLink() {
    const { code }   = useParams();
    const navigate   = useNavigate();
    const [gardener, setGardener] = useState(null);
    const [loading,  setLoading]  = useState(true);
    const [error,    setError]    = useState('');

    useEffect(() => {
        let cancelled = false;
        client.get(`/gardeners/by-code/${encodeURIComponent(code)}`)
            .then(r => {
                if (cancelled) return;
                setGardener(r.data.gardener);
                try {
                    sessionStorage.setItem(REFERRAL_KEY, code);
                } catch {
                    // Private browsing can block this. The button
                    // below passes the code in navigation state too,
                    // so the flow still works.
                }
            })
            .catch(() => { if (!cancelled) setError('That link is not valid.'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [code]);

    const requestQuote = () => {
        const token = localStorage.getItem('token');
        // Carry the code in navigation state as well as
        // sessionStorage, so a blocked write does not lose it.
        const state = { referralCode: code, gardener };
        navigate(token ? '/buyer/post-job' : '/register', { state });
    };

    if (loading) {
        return <div style={styles.page}><p style={styles.muted}>Loading...</p></div>;
    }

    if (error || !gardener) {
        return (
            <div style={styles.page}>
                <div style={styles.card}>
                    <h1 style={styles.title}>Link not found</h1>
                    <p style={styles.text}>
                        This link doesn't match any gardener. Check it was copied
                        in full, or browse local gardeners instead.
                    </p>
                    <Link to="/" style={styles.btnSecondary}>Go to GardenHand</Link>
                </div>
            </div>
        );
    }

    const name = `${gardener.first_name} ${gardener.last_name || ''}`.trim();

    return (
        <div style={styles.page}>
            <div style={styles.card}>
                <p style={styles.eyebrow}>You've been invited by</p>
                <h1 style={styles.title}>{name}</h1>

                <div style={styles.badges}>
                    {gardener.tier_label && (
                        <span style={styles.tier}>{gardener.tier_label}</span>
                    )}
                    {gardener.avg_rating > 0 && (
                        <span style={styles.stat}>
                            ⭐ {Number(gardener.avg_rating).toFixed(1)}
                        </span>
                    )}
                    {gardener.total_jobs > 0 && (
                        <span style={styles.stat}>{gardener.total_jobs} jobs completed</span>
                    )}
                    {gardener.suburb_name && (
                        <span style={styles.stat}>{gardener.suburb_name}</span>
                    )}
                </div>

                {gardener.bio && <p style={styles.bio}>{gardener.bio}</p>}

                {/* The promise that makes this link worth sharing.
                    Say it plainly to the buyer as well as the
                    gardener — it sets the expectation that this is
                    a direct booking, not an auction. */}
                <div style={styles.promise}>
                    Post your job here and it goes <strong>only to {gardener.first_name}</strong>.
                    Other gardeners won't see it.
                </div>

                {gardener.is_available === false && (
                    <div style={styles.warn}>
                        {gardener.first_name} isn't taking new jobs right now.
                        You can still post — they'll see it when they're back.
                    </div>
                )}

                <button
                    onClick={requestQuote}
                    style={styles.btn}
                    className="gh-touch-target"
                >
                    Request a quote from {gardener.first_name} →
                </button>

                <p style={styles.footer}>
                    GardenHand is free for customers. You'll be asked to describe
                    the job, and {gardener.first_name} will send you a price.
                </p>
            </div>
        </div>
    );
}

const styles = {
    page:         { maxWidth:'520px', margin:'0 auto', padding:'40px 24px' },
    card:         { background:'white', borderRadius:'12px', padding:'32px',
                    boxShadow:'0 2px 12px rgba(0,0,0,0.08)' },
    eyebrow:      { fontSize:'0.8rem', color:'#888', textTransform:'uppercase',
                    letterSpacing:'0.05em', margin:'0 0 4px', textAlign:'center' },
    title:        { color:'#2d6a4f', fontSize:'1.6rem', margin:'0 0 14px',
                    textAlign:'center' },
    badges:       { display:'flex', flexWrap:'wrap', gap:'8px',
                    justifyContent:'center', marginBottom:'16px' },
    tier:         { fontSize:'0.8rem', fontWeight:700, color:'#2d6a4f',
                    background:'#e8f5e9', padding:'4px 10px', borderRadius:'999px' },
    stat:         { fontSize:'0.8rem', color:'#555', background:'#f5f5f5',
                    padding:'4px 10px', borderRadius:'999px' },
    bio:          { fontSize:'0.9rem', color:'#555', lineHeight:1.6,
                    marginBottom:'18px', textAlign:'center' },
    promise:      { background:'#e8f5e9', border:'1px solid #a5d6a7',
                    borderRadius:'8px', padding:'12px 14px', fontSize:'0.88rem',
                    color:'#14532D', lineHeight:1.5, marginBottom:'16px',
                    textAlign:'center' },
    warn:         { background:'#FEF3C7', border:'1px solid #FCD34D',
                    borderRadius:'8px', padding:'10px 14px', fontSize:'0.85rem',
                    color:'#92400E', marginBottom:'16px' },
    btn:          { width:'100%', padding:'14px', backgroundColor:'#2d6a4f',
                    color:'white', border:'none', borderRadius:'8px',
                    fontSize:'1rem', fontWeight:'bold', cursor:'pointer' },
    btnSecondary: { display:'block', textAlign:'center', padding:'13px',
                    backgroundColor:'#f5f5f5', color:'#2d6a4f', borderRadius:'8px',
                    fontSize:'0.95rem', fontWeight:600, textDecoration:'none' },
    footer:       { fontSize:'0.8rem', color:'#999', textAlign:'center',
                    marginTop:'14px', marginBottom:0, lineHeight:1.5 },
    text:         { fontSize:'0.92rem', color:'#555', lineHeight:1.6,
                    marginBottom:'20px', textAlign:'center' },
    muted:        { textAlign:'center', color:'#888', padding:'40px' },
};
